import React, { useEffect, useRef } from 'react';
import { useCamera } from '../hooks/useCamera';
import { useFaceLandmarker } from '../hooks/useFaceLandmarker';
import { usePoseLandmarker } from '../hooks/usePoseLandmarker';
import { useObjectDetector } from '../hooks/useObjectDetector';
import { useHandLandmarker } from '../hooks/useHandLandmarker';
import type { PoseHeadEstimate } from '../hooks/usePoseLandmarker';
import type { PoseAuxInfo, PhoneGripAuxInfo } from '../hooks/useFaceLandmarker';
import type { Detection } from '../hooks/useObjectDetector';
import type { Point2D } from '../engine/Homography';

const VIDEO_ID = 'webcam';
const OVERLAY_ID = 'overlayCanvas';
const HAND_OVERLAY_ID = 'overlayCanvasHand';

/**
 * Minimal full-screen webcam view for demo capture.
 * Reuses the face/pose/object hooks from the main demo; the legacy zone
 * cone (not the head-pose ray) is enabled via IS_RECORDING_MODE.
 *
 * No calibration, no sidebars, no scene feed. Just the webcam with the
 * gaze-zone overlay drawn on top.
 */
export function RecordingApp() {
  // Skip WebEyeTracker handshake — start the camera immediately.
  useCamera(VIDEO_ID, 0);

  const detectionsRef = useRef<Detection[]>([]);
  const paperHomographyRef = useRef<Float64Array | null>(null);
  const paperCornersRef = useRef<Point2D[]>([]);
  const calibHomographyRef = useRef<Float64Array | null>(null);
  const calibCornersRef = useRef<Point2D[]>([]);
  const poseHeadRef = useRef<PoseHeadEstimate>({
    yaw: 0, pitch: 0, noseX: 0.5, noseY: 0.3, valid: false,
  });
  const poseAuxRef = useRef<PoseAuxInfo | null>(null);
  const phoneGripRef = useRef<PhoneGripAuxInfo | null>(null);

  useFaceLandmarker(
    VIDEO_ID, OVERLAY_ID, detectionsRef, paperHomographyRef,
    paperCornersRef, poseHeadRef, calibHomographyRef,
    calibCornersRef, poseAuxRef, phoneGripRef,
  );

  const pose = usePoseLandmarker(VIDEO_ID);
  poseHeadRef.current = pose.head;
  poseAuxRef.current = { ...pose.head, shoulderAngle: pose.shoulderAngle };

  const obj = useObjectDetector(VIDEO_ID, true);
  detectionsRef.current = obj.detections;

  // Hand landmarker — always on in recording view. Writes to its OWN
  // overlay canvas so the face landmarker's 30fps clearRect doesn't wipe
  // hand landmarks (which update at ~15fps).
  const hand = useHandLandmarker(VIDEO_ID, HAND_OVERLAY_ID, true);
  phoneGripRef.current = {
    grip: hand.phoneGrip,
    sustainFrames: hand.gripSustainFrames,
  };

  useEffect(() => {
    // Body-level styles for a clean fullscreen black backdrop.
    const prev = {
      bg: document.body.style.background,
      margin: document.body.style.margin,
      overflow: document.body.style.overflow,
    };
    document.body.style.background = '#000';
    document.body.style.margin = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.background = prev.bg;
      document.body.style.margin = prev.margin;
      document.body.style.overflow = prev.overflow;
    };
  }, []);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div className="video-wrap" style={{
        position: 'relative',
        aspectRatio: '4 / 3',
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '100%',
        height: '100%',
      }}>
        <video
          id={VIDEO_ID}
          autoPlay
          playsInline
          muted
          style={{
            position: 'absolute', top: 0, left: 0,
            width: '100%', height: '100%', objectFit: 'contain',
            transform: 'scaleX(-1)',
          }}
        />
        <canvas
          id={OVERLAY_ID}
          style={{
            position: 'absolute', top: 0, left: 0,
            width: '100%', height: '100%', objectFit: 'contain',
            pointerEvents: 'none',
          }}
        />
        <canvas
          id={HAND_OVERLAY_ID}
          style={{
            position: 'absolute', top: 0, left: 0,
            width: '100%', height: '100%', objectFit: 'contain',
            pointerEvents: 'none',
          }}
        />
        {/* Debug HUD: confirms hand detection is firing at all. */}
        <div style={{
          position: 'absolute', bottom: 12, left: 12,
          padding: '6px 12px', borderRadius: 6,
          background: 'rgba(0,0,0,0.65)', color: '#fff',
          font: '600 12px Pretendard, sans-serif', pointerEvents: 'none',
        }}>
          hand: {hand.handCount} · fps {hand.fps}
          {hand.phoneGrip.isGrip
            ? `  📱 grip (${hand.gripSustainFrames}f, ${Math.round(hand.phoneGrip.confidence * 100)}%)`
            : ''}
        </div>
      </div>
    </div>
  );
}

export default RecordingApp;
